import React, { useState } from 'react';
import { TranscriptTurn } from '../types';
import { useTranslation } from '../../../i18n';

interface ConversationTranscriptCardProps {
  transcript?: TranscriptTurn[];
  personaName?: string;
  learnerName?: string;
  onOpenModal?: () => void;
}

export const ConversationTranscriptCard: React.FC<ConversationTranscriptCardProps> = ({
  transcript = [],
  personaName,
  learnerName = 'You (Learner)',
  onOpenModal,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [filter, setFilter] = useState<'all' | 'learner' | 'ai'>('all');

  const isLearnerTurn = (t: any) => {
    const spk = String(t.speaker || t.role || '').toUpperCase();
    return spk === 'LEARNER' || spk === 'USER' || spk === 'ME' || spk === 'YOU';
  };

  const learnerCount = transcript.filter(isLearnerTurn).length;
  const aiCount = transcript.length - learnerCount;

  const filteredTurns = transcript.filter((turn) => {
    const isLearner = isLearnerTurn(turn);
    if (filter === 'learner') return isLearner;
    if (filter === 'ai') return !isLearner;
    return true;
  });

  return (
    <div
      className="scorecard-card"
      style={{
        background: 'var(--white)',
        border: '1px solid var(--line)',
        borderRadius: '14px',
        padding: 'clamp(14px, 3vw, 20px) clamp(12px, 3vw, 24px)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
        display: 'grid',
        gap: '14px',
        minWidth: 0,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Top Header Row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '7px',
              background: '#E8F0FE',
              color: '#0D4FBD',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg style={{ width: '16px', height: '16px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </span>
          <div>
            <h3 style={{ fontFamily: 'var(--display)', fontSize: '15px', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
              {t('scorecard.transcript_title', undefined, 'Conversation transcript')}
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--muted)' }}>
              {t('scorecard.transcript_sub', undefined, 'Click to load verbatim turns')}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 14px',
            borderRadius: '8px',
            border: '1px solid #E2E8F0',
            background: '#F1F5F9',
            color: 'var(--navy)',
            fontSize: '12.5px',
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
        >
          <span>👁️</span> {isExpanded ? t('scorecard.hide_transcript', undefined, 'Hide Transcript') : t('scorecard.show_transcript', undefined, 'Show Transcript')}
        </button>
      </div>

      {/* Expandable Verbatim Turns Section */}
      {isExpanded && (
        <div style={{ borderTop: '1px solid var(--line)', paddingTop: '14px', display: 'grid', gap: '12px' }}>
          {/* Filter Pills */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setFilter('all')}
                style={{
                  padding: '4px 10px',
                  borderRadius: '20px',
                  border: filter === 'all' ? '1px solid var(--blue)' : '1px solid var(--line)',
                  background: filter === 'all' ? 'var(--blue-soft)' : '#FFFFFF',
                  color: filter === 'all' ? 'var(--blue)' : 'var(--muted)',
                  fontSize: '11px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {t('scorecard.all_turns', { count: transcript.length }, `All Turns (${transcript.length})`)}
              </button>
              <button
                type="button"
                onClick={() => setFilter('learner')}
                style={{
                  padding: '4px 10px',
                  borderRadius: '20px',
                  border: filter === 'learner' ? '1px solid var(--blue)' : '1px solid var(--line)',
                  background: filter === 'learner' ? 'var(--blue-soft)' : '#FFFFFF',
                  color: filter === 'learner' ? 'var(--blue)' : 'var(--muted)',
                  fontSize: '11px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {t('scorecard.learner_turns', { count: learnerCount }, `Learner (${learnerCount})`)}
              </button>
              <button
                type="button"
                onClick={() => setFilter('ai')}
                style={{
                  padding: '4px 10px',
                  borderRadius: '20px',
                  border: filter === 'ai' ? '1px solid var(--blue)' : '1px solid var(--line)',
                  background: filter === 'ai' ? 'var(--blue-soft)' : '#FFFFFF',
                  color: filter === 'ai' ? 'var(--blue)' : 'var(--muted)',
                  fontSize: '11px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {t('scorecard.ai_turns', { count: aiCount }, `AI Persona (${aiCount})`)} {personaName ? `(${personaName})` : ''}
              </button>
            </div>

            {onOpenModal && (
              <button
                type="button"
                onClick={onOpenModal}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--blue)',
                  fontSize: '11.5px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {t('scorecard.open_full_modal', undefined, 'Open in Full Modal ↗')}
              </button>
            )}
          </div>

          {/* Turn Bubbles */}
          <div style={{ maxHeight: '340px', overflowY: 'auto', display: 'grid', gap: '8px', paddingRight: '4px' }}>
            {filteredTurns.length === 0 ? (
              <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--muted)', fontSize: '12.5px' }}>
                {t('scorecard.empty_turns_filter', undefined, 'No conversation turns recorded for this filter.')}
              </div>
            ) : (
              filteredTurns.map((turn, idx) => {
                const isLearner = isLearnerTurn(turn);
                const speakerDisplayName = isLearner
                  ? (turn.speakerName || learnerName || t('scorecard.learner_you', undefined, 'You (Learner)'))
                  : (turn.speakerName || (personaName ? `${t('scorecard.ai_customer', undefined, 'AI Persona')} (${personaName})` : t('scorecard.ai_customer', undefined, 'AI Persona')));
                return (
                  <div
                    key={idx}
                    style={{
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: isLearner ? '1px solid #BFDBFE' : '1px solid var(--line)',
                      background: isLearner ? '#EFF6FF' : '#F8FAFC',
                      display: 'grid',
                      gap: '4px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', fontWeight: 700 }}>
                      <span style={{ color: isLearner ? '#1D4ED8' : 'var(--navy)' }}>
                        {speakerDisplayName}
                      </span>
                      <span style={{ color: 'var(--faint)', fontSize: '10px' }}>
                        {t('scorecard.turn_n', { n: turn.turnNumber || idx + 1 }, `Turn #${turn.turnNumber || idx + 1}`)}
                      </span>
                    </div>
                    <p style={{ margin: 0, fontSize: '12px', color: 'var(--ink)', lineHeight: 1.45 }}>
                      {turn.text}
                    </p>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
